import { Badge, Button } from 'antd';
import {
  CaretRightOutlined,
  PauseOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { logger } from '@/services/logger.service';
import { ESessionStatus } from '@/shared/enums/ringing-status.enum';
import { State } from '@/services/state.service';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '@/components/../stores';
import { commonMakeCall } from '@/shared/helpers/common-make-call.helper';
import { CALL_PAGE_STATUS, CALL_PAGE_TYPE } from '@/shared/constants/callPage';
import { SipJs } from '@/services/sip-js.service';
import './DialerControl.scss';

export const DialerControl = observer(() => {
  const {
    settingsStore: { dialer },
    dialerStore: {
      statusDialer,
      numberOfCallee,
      firstPending,
      isDialerActive,
      isPendingCallee,
      changeCalleeStatus,
      toggleDialler,
      removeActiveCallee,
      setActiveCallee,
      switchToAnotherCallee,
      removePendingCallee,
    },
    callStore: { setCallPage, callPage },
  } = useStore();
  const location = useLocation();
  const navigate = useNavigate();

  const [gap, setGap] = useState<number>(0);

  const openDialerView = () => {
    const newPage =
      callPage.status === CALL_PAGE_STATUS.INACTIVE
        ? CALL_PAGE_TYPE.HIDDEN
        : CALL_PAGE_TYPE.MINIMIZED;

    setCallPage({ page: newPage });

    if (location.pathname.includes('/dialer-view')) {
      return;
    }

    navigate('/dialer-view');
  };

  const startSetGap = (value: number) => {
    if (value === 0) {
      return;
    }

    if (statusDialer === 'onPause') {
      return;
    }

    setGap(value);

    setTimeout(() => {
      startSetGap(value - 1);
    }, 1000);
  };

  useEffect(() => {
    State.setState({ isDialerActive });
  }, [isDialerActive]);

  useEffect(() => {
    const session = SipJs.sessions?.[0];

    if (!session?.sessionState$) {
      return;
    }

    const sub$ = session.sessionState$.subscribe((s) => {
      if (
        [
          ESessionStatus.PENDING,
          ESessionStatus.FAILED,
          ESessionStatus.COMPLETED,
          ESessionStatus.REJECTED,
        ].includes(s)
      ) {
        removeActiveCallee();
        switchToAnotherCallee();
      }
    });

    return () => {
      sub$ && sub$.unsubscribe();
    };
  }, [SipJs.sessions]);

  useEffect(() => {
    if (statusDialer === 'onPause') {
      return;
    }

    if (statusDialer === 'onGap') {
      const sec = dialer.wrapUpTime / 1000;
      startSetGap(sec);
      return;
    }

    if (!firstPending) {
      toggleDialler();

      return;
    }

    const { user } = firstPending;

    if (!user.phones[0]) {
      logger.warn(
        `[Dialer] there is no phone number in contact: ${user.fullName} !`,
      );

      changeCalleeStatus('completed', user);
      switchToAnotherCallee();

      return;
    }

    commonMakeCall([user], user.phones[0]);
    setActiveCallee(user);
  }, [statusDialer]);

  const stopDialer = () => {
    removePendingCallee();
  };

  const renderBtn = () => {
    if (!isPendingCallee) {
      return;
    }

    switch (statusDialer) {
      case 'onGap':
        return (
          <>
            <Button
              size="small"
              type="link"
              onClick={toggleDialler}
              icon={<PauseOutlined />}
            >
              {`${gap} sec`}
            </Button>
          </>
        );
      case 'onCall':
        return (
          <Button
            size="small"
            type="link"
            onClick={toggleDialler}
            icon={<PauseOutlined />}
          >
            Pause
          </Button>
        );
      case 'onPause':
        return (
          <Button
            size="small"
            type="link"
            onClick={toggleDialler}
            icon={<CaretRightOutlined />}
          >
            Continue
          </Button>
        );

      default:
        break;
    }
  };

  if (!isDialerActive) {
    return;
  }

  return (
    <div className="dialer-control">
      <div className="dialer-control__count" onClick={openDialerView}>
        <div>Call Campaign</div>
        <Badge count={numberOfCallee} />
      </div>
      <div className="dialer-control__btn">{renderBtn()}</div>
      {isPendingCallee && (
        <Button
          onClick={stopDialer}
          data-testid="stop-dialer"
          danger
          type="primary"
          shape="circle"
          icon={<CloseOutlined />}
          size={'small'}
        />
      )}
    </div>
  );
});
